/* In-house cookie consent.
 *
 * Responsibilities:
 *  - Banner / preferences-modal UI (markup in snippets/cookie-consent.liquid)
 *  - Google Consent Mode v2 updates (defaults are pushed synchronously in
 *    <head> by theme.liquid before the GTM stub — see window.__ccState)
 *  - Shopify Customer Privacy API (setTrackingConsent) so Shopify pixels and
 *    compliant app embeds follow the same choice
 *  - Global Privacy Control (GPC): auto-applies Do-Not-Sell/Share
 *  - First-party consent record cookie (utz_consent), versioned + expiring,
 *    mirrored to localStorage (Safari ITP caps JS cookies at ~7 days)
 *  - Tracking-cookie sweep on denial, consent-gated <script type="text/plain">
 *    activation, optional strict GTM hold release
 *
 * Consent semantics (must stay in lockstep with the head script in theme.liquid):
 *  - sale=false maps to Google as ad_user_data/ad_personalization denied +
 *    restricted_data_processing=true, even while marketing (ad_storage) is
 *    granted — the CPRA "measurement yes, sale/share no" posture.
 *  - GPC forces marketing+sale off unless the visitor has EXPLICITLY chosen
 *    otherwise (saleExplicit: they interacted with the Do-Not-Sell or
 *    Marketing toggle in the preferences modal). Accept-all does NOT override
 *    GPC, and never reverses a stored explicit sale opt-out.
 *  - An expired or version-bumped record re-prompts, but its denials are
 *    carried into the interim defaults — re-asking never re-grants.
 *
 * Public API: window.UtzConsent = { open, getConsent, isAllowed, onChange, withdraw }
 */
(function () {
  'use strict';

  var root = document.getElementById('cc-root');
  var cfgEl = document.getElementById('cc-config');
  if (!root || !cfgEl) return;

  var CFG;
  try {
    CFG = JSON.parse(cfgEl.textContent);
  } catch (e) {
    return;
  }

  var COOKIE_NAME = 'utz_consent';
  var CATS = ['analytics', 'marketing', 'functional'];

  // Known third-party cookies by category, swept when that category is denied.
  var SWEEP = {
    analytics: {
      exact: ['_ga', '_gid', '_gat', '_clck', '_clsk', 'CLID', 'ANONCHK', 'MUID', 'SM'],
      prefixes: ['_ga_', '_gat_']
    },
    marketing: {
      exact: ['_gcl_au', '_gcl_aw', '_gcl_dc', '_gcl_gb', '_fbp', '_fbc', 'ig_did', 'ig_nrcb', '_ttp', 'ttwid', '_tt_enable_cookie', '_uetsid', '_uetvid', '__attentive_id', '__attentive_cco', '__attentive_dv', '__attentive_pv', '__attentive_ss_referrer', 'attntv_mstore_email'],
      prefixes: ['_gac_', 'ttcsid', '__attentive']
    }
  };
  var SWEEP_STORAGE_PREFIXES = ['_scid', 'tt_', '_fbp', 'attn_'];

  var head = window.__ccState || {};
  var mode = head.mode === 'opt_in' || head.mode === 'opt_out' ? head.mode : 'opt_in';
  var gpc = head.gpc === true || navigator.globalPrivacyControl === true;
  var stale = head.stale || null; // expired/version-bumped record: honor its denials until re-choice
  var listeners = [];
  var lastFocused = null;

  /* ---------------- consent record ---------------- */

  function validRecord(rec) {
    if (!rec || typeof rec !== 'object' || !rec.cats) return null;
    if (String(rec.v) !== String(CFG.version)) return null;
    if (rec.ts && Date.now() - rec.ts > CFG.expiryDays * 864e5) return null;
    return rec;
  }

  function readRecord() {
    var rec = null;
    try {
      var m = document.cookie.match(/(?:^|; )utz_consent=([^;]*)/);
      if (m) rec = validRecord(JSON.parse(decodeURIComponent(m[1])));
    } catch (e) { rec = null; }
    if (rec) return rec;
    // localStorage mirror survives Safari ITP's ~7-day cap on JS cookies
    try {
      var ls = validRecord(JSON.parse(localStorage.getItem(COOKIE_NAME)));
      if (ls) { writeCookie(ls); return ls; }
    } catch (e) { /* no storage access */ }
    return null;
  }

  function cookieDomain() {
    var host = location.hostname;
    if (/^(\d{1,3}\.){3}\d{1,3}$/.test(host) || host.indexOf('.') === -1) return '';
    var parts = host.split('.');
    return '.' + parts.slice(-2).join('.');
  }

  function writeCookie(rec) {
    var value = encodeURIComponent(JSON.stringify(rec));
    var expires = new Date(Date.now() + CFG.expiryDays * 864e5).toUTCString();
    var base = COOKIE_NAME + '=' + value + '; path=/; expires=' + expires + '; SameSite=Lax; Secure';
    var domain = cookieDomain();
    if (domain) {
      // Kill any host-only shadow first so duplicates can't mask the new value,
      // then verify the domain write actually took (public-suffix domains like
      // *.myshopify.com are rejected by the browser) and fall back host-only.
      expireCookie(COOKIE_NAME, '');
      document.cookie = base + '; domain=' + domain;
      if (document.cookie.indexOf(COOKIE_NAME + '=' + value) === -1) document.cookie = base;
    } else {
      document.cookie = base;
    }
  }

  function writeRecord(rec) {
    writeCookie(rec);
    try { localStorage.setItem(COOKIE_NAME, JSON.stringify(rec)); } catch (e) { /* quota/denied */ }
  }

  /* ---------------- consent application ---------------- */

  function gtag() {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push(arguments);
  }

  function applyConsent(cats, saleAllowed, opts) {
    opts = opts || {};
    var mk = !!cats.marketing;
    var sl = !!saleAllowed;
    function s(b) { return b ? 'granted' : 'denied'; }
    gtag('consent', 'update', {
      ad_storage: s(mk),
      ad_user_data: s(mk && sl),
      ad_personalization: s(mk && sl),
      analytics_storage: s(!!cats.analytics),
      functionality_storage: s(!!cats.functional),
      personalization_storage: s(!!cats.functional),
      security_storage: 'granted'
    });
    if (CFG.adsRedaction) gtag('set', 'ads_data_redaction', !(mk && sl));
    // Google Ads restricted data processing — the CPRA sale/share opt-out signal
    gtag('set', { restricted_data_processing: !sl });

    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: 'cookie_consent_update',
      consent_analytics: !!cats.analytics,
      consent_marketing: mk,
      consent_functional: !!cats.functional,
      consent_sale_of_data: sl
    });

    shopifyPrivacy(cats, sl, opts.shopifyDenialsOnly);
    sweepOnDenial(cats);
    activateGatedScripts(cats);

    var detail = {
      analytics: !!cats.analytics,
      marketing: mk,
      functional: !!cats.functional,
      saleAllowed: sl
    };
    try {
      window.dispatchEvent(new CustomEvent('utz:consent:changed', { detail: detail }));
    } catch (e) { /* older browsers */ }
    listeners.forEach(function (cb) {
      try { cb(detail); } catch (e) { /* listener error is not our problem */ }
    });
  }

  function shopifyPrivacy(cats, saleAllowed, denialsOnly) {
    // denialsOnly: GPC auto-opt-out path — communicate only the opt-outs, never
    // assert consent the visitor didn't give.
    var payload = denialsOnly
      ? { marketing: false, sale_of_data: false }
      : {
          analytics: !!cats.analytics,
          marketing: !!cats.marketing,
          preferences: !!cats.functional,
          sale_of_data: !!saleAllowed
        };
    try {
      if (window.Shopify && window.Shopify.customerPrivacy) {
        window.Shopify.customerPrivacy.setTrackingConsent(payload, function () {});
      } else if (window.Shopify && window.Shopify.loadFeatures) {
        window.Shopify.loadFeatures(
          [{ name: 'consent-tracking-api', version: '0.1' }],
          function (err) {
            if (!err && window.Shopify.customerPrivacy) {
              window.Shopify.customerPrivacy.setTrackingConsent(payload, function () {});
            }
          }
        );
      }
    } catch (e) { /* Shopify API unavailable — GCM still governs GTM tags */ }
  }

  function expireCookie(name, domain) {
    var base = name + '=; path=/; expires=Thu, 01 Jan 1970 00:00:00 GMT';
    document.cookie = domain ? base + '; domain=' + domain : base;
  }

  function sweepOnDenial(cats) {
    var domain = cookieDomain();
    var names = document.cookie.split(';').map(function (c) {
      return c.trim().split('=')[0];
    });
    ['analytics', 'marketing'].forEach(function (cat) {
      if (cats[cat]) return;
      names.forEach(function (name) {
        var hit =
          SWEEP[cat].exact.indexOf(name) > -1 ||
          SWEEP[cat].prefixes.some(function (p) { return name.indexOf(p) === 0; });
        if (hit) {
          expireCookie(name, '');
          if (domain) expireCookie(name, domain);
        }
      });
    });
    if (!cats.marketing) {
      try {
        for (var i = localStorage.length - 1; i >= 0; i--) {
          var key = localStorage.key(i);
          if (SWEEP_STORAGE_PREFIXES.some(function (p) { return key.indexOf(p) === 0; })) {
            localStorage.removeItem(key);
          }
        }
      } catch (e) { /* storage denied */ }
    }
  }

  // <script type="text/plain" data-cc-consent="marketing" src="..."> pattern:
  // inert until its category is granted, then activated exactly once.
  function activateGatedScripts(cats) {
    var gated = document.querySelectorAll('script[type="text/plain"][data-cc-consent]');
    Array.prototype.forEach.call(gated, function (el) {
      var cat = el.getAttribute('data-cc-consent');
      if (!cats[cat] || el.hasAttribute('data-cc-activated')) return;
      el.setAttribute('data-cc-activated', '');
      var s = document.createElement('script');
      Array.prototype.forEach.call(el.attributes, function (attr) {
        if (attr.name !== 'type' && attr.name !== 'data-cc-consent') s.setAttribute(attr.name, attr.value);
      });
      s.type = 'text/javascript';
      if (!el.src) s.textContent = el.textContent;
      el.parentNode.insertBefore(s, el.nextSibling);
    });
  }

  /* ---------------- defaults (mirror the head script's rules) ---------------- */

  function staleDenied(cat) {
    return stale && stale.cats && stale.cats[cat] === false;
  }

  function defaultCats() {
    var granted = mode === 'opt_out';
    return {
      analytics: granted && !staleDenied('analytics'),
      marketing: granted && !gpc && !staleDenied('marketing'),
      functional: granted && !staleDenied('functional')
    };
  }

  function defaultSale() {
    if (mode !== 'opt_out' || gpc) return false;
    return !(stale && stale.sale === false);
  }

  /* ---------------- UI helpers ---------------- */

  function $(sel) { return root.querySelector(sel); }
  function $all(sel) { return Array.prototype.slice.call(root.querySelectorAll(sel)); }

  var banner = $('#cc-banner');
  var overlay = $('#cc-overlay');
  var modal = $('#cc-modal');
  var reopen = $('#cc-reopen');
  var live = $('#cc-live');

  function announce(text) {
    if (!live) return;
    live.textContent = '';
    window.setTimeout(function () { live.textContent = text; }, 50);
  }

  function showBanner() {
    var msg = mode === 'opt_in' ? 'opt_in' : 'opt_out';
    $all('[data-cc-message]').forEach(function (el) {
      el.hidden = el.getAttribute('data-cc-message') !== msg;
    });
    if (gpc) {
      $all('[data-cc-gpc-note]').forEach(function (el) { el.hidden = false; });
    }
    banner.hidden = false;
    if (reopen) reopen.hidden = true;
    // Keep keyboard focus from landing behind the fixed bottom bar (WCAG 2.4.11)
    window.requestAnimationFrame(function () {
      document.documentElement.style.scrollPaddingBottom = banner.offsetHeight + 16 + 'px';
    });
  }

  function hideBanner() {
    banner.hidden = true;
    document.documentElement.style.scrollPaddingBottom = '';
    if (reopen && CFG.showReopen) reopen.hidden = false;
  }

  /* ---------------- modal (true dialog: trap + inert background) ---------------- */

  var inerted = [];

  function setBackgroundInert(on) {
    if (on) {
      Array.prototype.forEach.call(document.body.children, function (el) {
        if (el === root || el.tagName === 'SCRIPT' || el.tagName === 'STYLE') return;
        if (el.inert) return; // already inert (someone else's modal) — leave it alone
        el.inert = true;
        inerted.push(el);
      });
      // Inside #cc-root, everything except the dialog itself goes inert too
      if (!banner.hidden) banner.inert = true;
      if (reopen) reopen.inert = true;
    } else {
      inerted.forEach(function (el) { el.inert = false; });
      inerted = [];
      banner.inert = false;
      if (reopen) reopen.inert = false;
    }
  }

  function focusables(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll('a[href], button, input, select, textarea, [tabindex]:not([tabindex="-1"])'),
      function (el) { return !el.disabled && !!(el.offsetWidth || el.offsetHeight); }
    );
  }

  function trapKeydown(e) {
    if (e.key === 'Escape' || e.key === 'Esc') {
      e.preventDefault();
      closeModal();
      return;
    }
    if (e.key !== 'Tab') return;
    var items = focusables(modal);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && (document.activeElement === first || document.activeElement === modal || document.activeElement === $('#cc-modal-title'))) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  function openModal(focusDns) {
    var target;
    if (!overlay.hidden) {
      // already open — just honor the focus request
      target = focusDns && $('[data-cc-dns]') ? $('[data-cc-dns]') : $('#cc-modal-title');
      target.focus();
      return;
    }
    lastFocused = document.activeElement;
    syncToggles();
    if (gpc) {
      $all('[data-cc-gpc-note]').forEach(function (el) { el.hidden = false; });
    }
    overlay.hidden = false;
    setBackgroundInert(true);
    document.documentElement.style.overflow = 'hidden';
    document.addEventListener('keydown', trapKeydown, true);
    target = focusDns && $('[data-cc-dns]') ? $('[data-cc-dns]') : $('#cc-modal-title');
    target.focus();
  }

  function teardownModal() {
    overlay.hidden = true;
    setBackgroundInert(false);
    document.documentElement.style.overflow = '';
    document.removeEventListener('keydown', trapKeydown, true);
  }

  function closeModal() {
    teardownModal();
    var back = lastFocused && document.contains(lastFocused) && lastFocused !== document.body
      ? lastFocused
      : (!banner.hidden ? banner.querySelector('button') : (reopen && !reopen.hidden ? reopen : null));
    if (back) back.focus();
    lastFocused = null;
  }

  function syncToggles() {
    var rec = readRecord();
    var cats = rec ? rec.cats : defaultCats();
    $all('[data-cc-cat]').forEach(function (input) {
      input.checked = !!cats[input.getAttribute('data-cc-cat')];
      input.dataset.init = String(input.checked);
    });
    var dns = $('[data-cc-dns]');
    if (dns) {
      var saleAllowed = rec ? rec.sale !== false : defaultSale();
      dns.checked = !saleAllowed; // checkbox = "do NOT sell", checked means opted out
      dns.dataset.init = String(dns.checked);
    }
  }

  /* ---------------- actions ---------------- */

  function record(cats, saleAllowed, action, saleExplicit, opts) {
    var rec = {
      v: String(CFG.version),
      ts: Date.now(),
      mode: mode,
      gpc: gpc,
      action: action,
      cats: { analytics: !!cats.analytics, marketing: !!cats.marketing, functional: !!cats.functional },
      sale: !!saleAllowed,
      saleExplicit: !!saleExplicit
    };
    writeRecord(rec);
    applyConsent(rec.cats, rec.sale, opts);
    return rec;
  }

  function acceptAll() {
    var prev = readRecord();
    var explicit = !!(prev && prev.saleExplicit);
    var cats = { analytics: true, marketing: true, functional: true };
    var saleAllowed;
    if (explicit) {
      // Never silently reverse an explicit Do-Not-Sell choice
      saleAllowed = prev.sale !== false;
    } else if (gpc) {
      // GPC governs until the visitor makes an explicit sale/marketing choice
      // (same rule as the head defaults script — keeps page loads consistent)
      saleAllowed = false;
      cats.marketing = false;
    } else {
      saleAllowed = true;
    }
    record(cats, saleAllowed, 'accept_all', explicit);
    finishChoice();
  }

  function rejectAll() {
    var prev = readRecord();
    record(
      { analytics: false, marketing: false, functional: false },
      false,
      'reject_all',
      !!(prev && prev.saleExplicit)
    );
    finishChoice();
  }

  function savePreferences() {
    var prev = readRecord();
    var cats = {};
    var changedPrivacyToggle = false;
    $all('[data-cc-cat]').forEach(function (input) {
      cats[input.getAttribute('data-cc-cat')] = input.checked;
      if (input.getAttribute('data-cc-cat') === 'marketing' && String(input.checked) !== input.dataset.init) {
        changedPrivacyToggle = true;
      }
    });
    var dns = $('[data-cc-dns]');
    if (dns && String(dns.checked) !== dns.dataset.init) changedPrivacyToggle = true;
    var saleExplicit = !!(prev && prev.saleExplicit) || changedPrivacyToggle;
    var saleAllowed = dns ? !dns.checked : cats.marketing;
    if (gpc && !saleExplicit) {
      // untouched toggles under GPC keep the GPC opt-out
      saleAllowed = false;
      cats.marketing = false;
    }
    record(cats, saleAllowed, 'custom', saleExplicit);
    finishChoice();
  }

  function finishChoice() {
    if (!overlay.hidden) teardownModal();
    hideBanner();
    announce(CFG.strings.saved);
    if (reopen && CFG.showReopen) {
      reopen.hidden = false;
      reopen.focus();
    } else {
      var main = document.getElementById('MainContent');
      if (main) main.focus();
    }
    lastFocused = null;
  }

  /* ---------------- wiring ---------------- */

  root.addEventListener('click', function (e) {
    var btn = e.target.closest('[data-cc-action]');
    if (!btn) return;
    var action = btn.getAttribute('data-cc-action');
    if (action === 'accept-all') acceptAll();
    else if (action === 'reject-all') rejectAll();
    else if (action === 'save') savePreferences();
    else if (action === 'open-preferences') openModal(false);
    else if (action === 'close-preferences') closeModal();
  });

  // Clicking the dimmed backdrop closes without saving (same as Escape).
  // click (not mousedown) so a press can still be aborted by sliding off
  // (WCAG 2.5.2 Pointer Cancellation).
  overlay.addEventListener('click', function (e) {
    if (e.target === overlay) closeModal();
  });

  // Footer links (and any element with .js-cookie-preferences) open the modal;
  // data-cc-focus="dns" jumps straight to the Do-Not-Sell toggle.
  document.addEventListener('click', function (e) {
    var link = e.target.closest('.js-cookie-preferences');
    if (!link) return;
    e.preventDefault();
    openModal(link.getAttribute('data-cc-focus') === 'dns');
  });

  if (location.hash === '#cookie-preferences') openModal(false);
  window.addEventListener('hashchange', function () {
    if (location.hash === '#cookie-preferences') openModal(false);
  });

  /* ---------------- public API ---------------- */
  /* Defined before init: consent-gated scripts activated during init (e.g. the
     MikMak loader) read UtzConsent.isAllowed() the moment they execute. */

  window.UtzConsent = {
    open: function (focusDns) { openModal(!!focusDns); },
    getConsent: function () {
      var r = readRecord();
      return r ? { cats: r.cats, saleAllowed: r.sale, action: r.action, ts: r.ts } : null;
    },
    isAllowed: function (cat) {
      if (cat === 'necessary') return true;
      var r = readRecord();
      return r ? !!r.cats[cat] : !!defaultCats()[cat];
    },
    onChange: function (cb) { if (typeof cb === 'function') listeners.push(cb); },
    withdraw: function () {
      rejectAll();
    }
  };

  /* ---------------- init ---------------- */

  var rec = readRecord();

  if (rec) {
    // Returning visitor with a valid record: re-apply (keeps Shopify's API and
    // gated scripts in sync). A GPC signal constrains marketing/sale unless the
    // visitor explicitly chose otherwise — the SAME rule the head defaults
    // script applies, so defaults and updates never disagree across loads.
    if (gpc && !rec.saleExplicit && (rec.sale !== false || rec.cats.marketing)) {
      rec.sale = false;
      rec.gpc = true;
      rec.cats.marketing = false;
      writeRecord(rec);
    }
    applyConsent(rec.cats, rec.sale);
    if (rec.action === 'gpc') showBanner();
    else if (reopen && CFG.showReopen) reopen.hidden = false;
  } else if (gpc) {
    // Honor GPC immediately as a Do-Not-Sell/Share opt-out, recorded as such;
    // the banner still shows so the visitor can make their full choice. Only
    // the opt-outs are sent to Shopify — nothing is asserted as consented.
    record(defaultCats(), false, 'gpc', false, { shopifyDenialsOnly: true });
    showBanner();
  } else {
    if (mode === 'opt_out') {
      // Opt-out model: defaults are already "granted" via the head script
      // (minus anything a stale record had denied). No Shopify call before an
      // actual user action — no fabricated consent records.
      activateGatedScripts(defaultCats());
    }
    showBanner();
  }
})();
